<template>
    <v-container fluid class="p-20">
        <div style="display: flex; align-items: center; justify-content: flex-end; margin-bottom: 12px">
            <TimeRangePicker ref="TimeRangePicker" v-model="timeRange" button-class-name="ml-0"></TimeRangePicker>
            <div style="width: 350px; margin-left: 8px">
                <v-autocomplete
                    hide-details outlined
                    :placeholder="$f.tc('action')"
                    :items="actionList"
                    v-model="requestAction"
                    class="input-custom"
                    clearable
                ></v-autocomplete>
            </div>
            <div style="width: 250px; margin-left: 8px">
                <v-text-field
                    hide-details
                    :placeholder="$f.tc('user')"
                    v-model="userEmail"
                    class="input-custom"
                ></v-text-field>
            </div>
            <div
                class="ml-8"
                :class="isLoadingRefresh ? 'btn-disable' : 'btn-custom'"
                @click="refresh"
            >
                <v-progress-circular
                    indeterminate
                    width="3"
                    color="grey"
                    class="mr-6"
                    style="width: 15px"
                    v-if="isLoadingRefresh"
                ></v-progress-circular>
                <v-icon v-else style="margin-right: 6px; color: #FFFFFF; width: 15px">mdi-filter-outline</v-icon>
                {{ $f.tc('filter') }} / {{ $f.tc('refresh') }}
            </div>
        </div>
        <SimpleDataTable
            :headers="headers"
            :items="items"
            :pagination.sync="pagination"
        >
            <template v-slot:customPath="props">
                <div class="centerY">
                    {{ mapPathToAction(props.item) }}
                    <!--Button copy api path-->
                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                            <div class="btn-icon-2" v-on="on"
                                 @click="copyText(props.item['request.path'])">
                                <img src="/img/scenario/copy.svg" alt="icon-copy" style="transform: scale(0.7)">
                            </div>
                        </template>
                        <span>{{ props.item['request.path'] }}</span>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:action="props">
                <v-btn @click="openDataViewer(props.item)">Data</v-btn>
            </template>
        </SimpleDataTable>
        <JSONViewer ref="JSONViewer"></JSONViewer>
    </v-container>
</template>

<script>
import CampaignService from '@/services/campaign.service'
import SimpleDataTable from '@/modules/fptai/components/SimpleDataTable'
import JSONViewer from '@/components/JSONViewer'
import TimeRangePicker from '@/modules/fptai/components/TimeRangePicker'
import $const from '@/utils/const'

export default {
    name: 'CampaignAudit',
    components: { JSONViewer, SimpleDataTable, TimeRangePicker },
    data () {
        return {
            pagination: {
                page: 1,
                rowsPerPage: 10,
                totalItems: 0,
                ordering: '-@timestamp'
            },
            items: [],
            timeRange: 'thisYear',
            userEmail: '',
            requestMethod: '',
            methodList: ['POST', 'PUT', 'PATCH', 'DELETE'],
            isLoadingRefresh: false,
            requestAction: '',
        }
    },
    computed: {
        headers () {
            return this.$appService.correctHeaders([
                {
                    text: this.$t('time'),
                    value: '@timestamp',
                    sortable: true,
                },
                {
                    text: this.$t('user'),
                    value: 'user.email',
                },
                {
                    text: this.$t('method'),
                    value: 'request.method',
                },
                {
                    text: this.$t('action'),
                    value: 'request.path',
                    type: 'custom',
                    id: 'customPath',
                },
                {
                    text: this.$t('requestData'),
                    value: 'action',
                    type: 'custom',
                    id: 'action',
                }
            ])
        },
        actionList () {
            return [
                {
                    text: $const.campaign.actionAuditList.CREATE_PERMISSION,
                    value: 'POST' + ';' + 'users-assign/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/users-assign\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.CREATE_CAMPAIGN_SCHEDULE,
                    value: 'POST' + ';' + 'schedules/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/schedules\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_CAMPAIGN_SCHEDULE,
                    value: 'PUT' + ';' + 'schedules/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/schedules\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.PUT,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_CAMPAIGN_SCHEDULE,
                    value: 'DELETE' + ';' + 'schedules/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/schedules\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.CREATE_VOICE_PROFILE,
                    value: 'POST' + ';' + 'voice-speed/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/voice-speed\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_VOICE_PROFILE,
                    value: 'PATCH' + ';' + 'voice-speed/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/voice-speed\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.PATCH,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_VOICE_PROFILE,
                    value: 'DELETE' + ';' + 'voice-speed/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/voice-speed\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.CREATE_CALL_TREATMENT_CONFIG,
                    value: 'POST' + ';' + 'treatment-config/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/treatment-config\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_CALL_TREATMENT_CONFIG,
                    value: 'PUT' + ';' + 'treatment-config/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/treatment-config\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.PUT,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_CALL_TREATMENT_CONFIG,
                    value: 'DELETE' + ';' + 'treatment-config/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/treatment-config\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.CREATE_AUTO_REPORT_CONFIG,
                    value: 'POST' + ';' + 'config-report-via-email/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/config-report-via-email\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_AUTO_REPORT_CONFIG,
                    value: 'PUT' + ';' + 'config-report-via-email/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/config-report-via-email\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.PUT,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_AUTO_REPORT_CONFIG,
                    value: 'DELETE' + ';' + 'config-report-via-email/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/config-report-via-email\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.CREATE_BLACKLIST_NUMBER,
                    value: 'POST' + ';' + 'black-list/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/black-list\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_BLACKLISTED_NUMBER,
                    value: 'DELETE' + ';' + 'black-list/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/black-list\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                { // Delete all blacklist
                    text: $const.campaign.actionAuditList.DELETE_BLACKLISTED_NUMBER,
                    value: 'POST' + ';' + 'delete-all-black-list/',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/delete-all-black-list\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                { // Delete selected blacklist
                    text: $const.campaign.actionAuditList.DELETE_BLACKLISTED_NUMBER,
                    value: 'PUT' + ';' + 'delete-selected-black-list/',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/delete-selected-black-list\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_GENERAL_CAMPAIGN_SETTING,
                    value: 'PATCH' + ';',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.PATCH,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_CAMPAIGN_CONFIG,
                    value: 'PUT' + ';' + 'config/',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/config\/$/g,
                    method: $const.HttpMethodsMap.PUT,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_CAMPAIGN_STATUS,
                    value: 'POST' + ';' + 'status/',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/status\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.CREATE_CUSTOMER,
                    value: 'POST' + ';' + 'customers/',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/customers\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_A_CUSTOMER,
                    value: 'DELETE' + ';' + 'customers/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/customers\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_CUSTOMERS_IN_A_PERIOD,
                    value: 'DELETE' + ';' + 'delete-from-to-customers/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/delete-from-to-customers\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_ALL_CUSTOMERS,
                    value: 'DELETE' + ';' + 'delete-all-customers/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/delete-all-customers\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.CREATE_CUSTOMER_PROPERTY,
                    value: 'POST' + ';' + 'customer-properties/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/customer-properties\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.UPDATE_CUSTOMER_PROPERTY,
                    value: 'PUT' + ';' + 'customer-properties/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/customer-properties\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.PUT,
                },
                {
                    text: $const.campaign.actionAuditList.DELETE_CUSTOMER_PROPERTY,
                    value: 'DELETE' + ';' + 'customer-properties/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/customer-properties\/[0-9a-f-]+\/$/g,
                    method: $const.HttpMethodsMap.DELETE,
                },
                {
                    text: $const.campaign.actionAuditList.CALL_OUT,
                    value: 'POST' + ';' + 'customers/*/call-out/',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/customers\/[0-9a-f-]+\/call-out\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.UPLOAD_CALL_LIST,
                    value: 'POST' + ';' + 'import-customers/*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/import-customers\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.campaign.actionAuditList.CHECK_USER_CAMPAIGN,
                    value: 'POST' + ';' + 'check-user-campaign/',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\/check-user-campaign\/$/g,
                    method: $const.HttpMethodsMap.POST,
                },
                // other case
                {
                    text: $const.HttpMethodsMap.POST,
                    value: 'POST' + ';' + '*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\//g,
                    method: $const.HttpMethodsMap.POST,
                },
                {
                    text: $const.HttpMethodsMap.PATCH,
                    value: 'PATCH' + ';' + '*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\//g,
                    method: $const.HttpMethodsMap.PATCH,
                },
                {
                    text: $const.HttpMethodsMap.PUT,
                    value: 'PUT' + ';' + '*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\//g,
                    method: $const.HttpMethodsMap.PUT,
                },
                {
                    text: $const.HttpMethodsMap.DELETE,
                    value: 'DELETE' + ';' + '*',
                    regex: /\/api\/campaigns\/[0-9a-f-]+\//g,
                    method: $const.HttpMethodsMap.DELETE,
                },
            ]
        }
    },
    mounted () {
        this.refresh()
    },
    methods: {
        buildRegex () {
            const campaignId = this.$route.params.campaignId
            return '/api/campaigns/' + campaignId + '/*'
        },
        refresh () {
            this.isLoadingRefresh = true
            CampaignService.audit(
                this.$route.params.campaignId,
                {
                    ...this.$refs.TimeRangePicker.getTimestamp(),
                    ...this.pagination,
                    regex: this.buildRegex(),
                    request_method: this.requestMethod || '*',
                    user_email: this.userEmail ? `*${this.userEmail}*` : '*',
                    request_action: this.requestAction,
                }
            ).then(
                result => {
                    this.pagination.totalItems = result.data.count
                    this.items = result.data.data.map(item => ({
                        '@timestamp': this.$filters.formatDateTimeLocal(item['@timestamp']),
                        'user.email': item.user.email,
                        'request.method': item.request.method,
                        'request.path': item.request.path,
                        'request.data': item.request.data,
                        response_status: item.response.status,
                    }))
                }
            ).finally(() => {
                this.isLoadingRefresh = false
            })
        },
        openDataViewer (item) {
            this.$refs.JSONViewer.open(item['request.data'])
        },
        mapPathToAction (item) {
            let APIs = this.actionList.filter(i => i.method === item['request.method'])
            if (APIs.length === 0) {
                return item['request.path']
            }

            for (let API of APIs) {
                if (item['request.path'].match(API.regex)) {
                    return API.text
                }
            }

            return item['request.path']
        },
        copyText (text) {
            this.$utils.copyToClipboard(text, this.$f.tc('copied'))
        },
    },
    watch: {
        pagination: 'refresh'
    }
}
</script>

<style scoped lang="scss">
.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;

            &:before, &:after {
                border: none !important;
            }
        }

        .v-label {
            background: #FFFFFF !important;
            padding: 0 6px;

            &:not(.v-label--active) {
                font-size: 14px !important;
                padding-left: 0 !important;
            }
        }
    }
}

.btn-disable {
    font-family: "open sans", serif !important;
}

::v-deep {
    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }

    table {
        border: 1px solid #96add0;
    }

    th {
        color: #12274f !important;
        background: #e4edfa;
    }

    .v-datatable__progress {
        display: none;
    }

    .pager-wrap {
        border-top: 1px solid #96add0 !important;
    }
}
</style>
